import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, useAuth } from "../App";
import { toast } from "sonner";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import { Check, X, Mail, Ban, Clock, CheckCircle, Search, Key, Trash2 } from "lucide-react";
import { useHints } from "../components/HintsContext";

const STATUS_BADGE = {
    approved: { color: "#22C55E", label: "Approved", icon: CheckCircle },
    pending: { color: "#EAB308", label: "Pending", icon: Clock },
    revoked: { color: "#EF4444", label: "Revoked", icon: Ban },
};

const ROLE_OPTIONS = [
    { value: "Admin", label: "Admin (Full Access)" },
    { value: "SrManager", label: "Senior Manager" },
    { value: "CreativeDirector", label: "Creative Director" },
    { value: "AssociateCreativeDirector", label: "Assoc. Creative Director" },
    { value: "ContentProducer", label: "Content Producer" },
    { value: "SocialMediaManager", label: "Social Media Manager" },
    { value: "Manager", label: "Manager" },
    { value: "Executive", label: "Executive" },
    { value: "Associate", label: "Associate" },
    { value: "JrAssociate", label: "Jr. Associate" },
    { value: "Intern", label: "Intern" },
    { value: "Member", label: "Member" },
];
const EMPTY_INVITE = { name: "", email: "", role: "Member", modules: ["hr", "bizdev"] };

export default function AdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("all");
    const [showInvite, setShowInvite] = useState(false);
    const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
    const [inviting, setInviting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [resetUser, setResetUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [resetting, setResetting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [newDeptName, setNewDeptName] = useState("");
    const [newDeptColor, setNewDeptColor] = useState("#64748B");
    const [managers, setManagers] = useState([]);
    const { enabled: hintsEnabled, setEnabled: setHintsEnabled, reset: resetHints } = useHints();
    // Share.market brief entries — fetched lazily when the tab is opened.
    const [smEntries, setSmEntries] = useState([]);
    const [smLoading, setSmLoading] = useState(false);
    const [smExpanded, setSmExpanded] = useState(null); // sm_id of expanded row

    useEffect(() => {
        const rk = (r) => (r || "").toLowerCase();
        if (rk(user?.role) !== "admin" && !isSuperAdmin(user?.email)) { navigate("/me"); return; }
        loadUsers();
        loadDepartments();
        loadManagers();
    }, [user]);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    // Lazy-load share.market entries the first time the tab is opened.
    useEffect(() => {
        if (tab !== "share-market") return;
        setSmLoading(true);
        api.get("/admin/share-market/entries")
            .then(r => setSmEntries(r.data?.entries || []))
            .catch(() => toast.error("Couldn't load share.market entries"))
            .finally(() => setSmLoading(false));
    }, [tab]);

    const downloadSmCsv = async () => {
        try {
            const r = await api.get("/admin/share-market/entries.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `share-market-entries-${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            toast.error("CSV download failed");
        }
    };

    const loadDepartments = async () => {
        try { const r = await api.get("/departments"); setDepartments(r.data || []); } catch {}
    };
    const addDepartment = async () => {
        if (!newDeptName.trim()) return;
        try { await api.post("/departments", { name: newDeptName.trim(), color: newDeptColor }); setNewDeptName(""); loadDepartments(); toast.success("Department added"); } catch (e) { toast.error("Failed"); }
    };
    const deleteDepartment = (deptId) => {
        const snap = departments;
        const dept = (departments || []).find(d => (d.dept_id || d._id || d.id) === deptId);
        // Optimistic: strip from list
        setDepartments(prev => (prev || []).filter(d => (d.dept_id || d._id || d.id) !== deptId));
        let undone = false;
        toast(`Deleted: ${dept?.name || "department"}`, {
            duration: 5000,
            action: { label: "Undo", onClick: () => { undone = true; setDepartments(snap); } },
            onAutoClose: () => {
                if (undone) return;
                setTimeout(async () => {
                    try { await api.delete(`/departments/${deptId}`); }
                    catch { setDepartments(snap); toast.error("Delete failed — restored"); }
                }, 50);
            },
            onDismiss: () => {
                if (undone) return;
                setTimeout(async () => {
                    try { await api.delete(`/departments/${deptId}`); }
                    catch { setDepartments(snap); toast.error("Delete failed — restored"); }
                }, 50);
            },
        });
    };
    const updateUserDepartment = async (userId, dept) => {
        try { await api.put(`/users/${userId}/department`, { department: dept }); toast.success("Department updated"); loadUsers(); } catch { toast.error("Failed"); }
    };
    const loadManagers = async () => {
        try { const r = await api.get("/admin/managers"); setManagers(r.data || []); } catch {}
    };
    const updateReportsTo = async (userId, managerId) => {
        try { await api.put(`/admin/users/${userId}/reports-to`, { reports_to: managerId || null }); toast.success("Manager updated"); loadUsers(); } catch { toast.error("Failed to update manager"); }
    };
    const updateDesignation = async (userId, designation) => {
        try { await api.put(`/admin/users/${userId}/designation`, { designation }); toast.success("Designation updated"); loadUsers(); } catch { toast.error("Failed to update designation"); }
    };

    const loadUsers = async () => {
        try {
            const res = await api.get("/users");
            setUsers(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const updateStatus = async (userId, status) => {
        try {
            await api.put(`/admin/users/${userId}/status`, { status });
            toast.success(`User ${status === "approved" ? "approved" : status === "revoked" ? "revoked" : "set to pending"}`);
            loadUsers();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : JSON.stringify(e.response?.data?.detail) || "Failed"); }
    };

    const toggleModule = async (userId, module, currentModules) => {
        const modules = currentModules || ["hr", "bizdev"];
        const newModules = modules.includes(module) ? modules.filter((m) => m !== module) : [...modules, module];
        try {
            await api.put(`/admin/users/${userId}/modules`, { modules: newModules });
            toast.success("Modules updated");
            loadUsers();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : JSON.stringify(e.response?.data?.detail) || "Failed"); }
    };

    // Bulk-set all modules at once — saves ~60 clicks/session per the audit
    const setAllModules = async (userId, modules) => {
        try {
            await api.put(`/admin/users/${userId}/modules`, { modules });
            toast.success(modules.length ? `All modules enabled` : `All modules cleared`);
            loadUsers();
        } catch (e) { toast.error("Failed"); }
    };

    
  const toggleRevenueAccess = async (userId, currentAccess) => {
    try {
              const res = await api.put(`/admin/users/${userId}/revenue-access`, { revenue_access: !currentAccess });
              
      setUsers(prev => prev.map(u => (u.id || u.user_id) === userId ? { ...u, revenue_access: res.data.revenue_access } : u));
      toast.success(!currentAccess ? "Revenue access granted" : "Revenue access revoked");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : JSON.stringify(e.response?.data?.detail) || "Failed to update revenue access");
    }
  };

  const toggleFunnelAccess = async (userId, currentAccess) => {
    try {
      const res = await api.put(`/admin/users/${userId}/funnel-access`, { funnel_access: !currentAccess });
      setUsers(prev => prev.map(u => (u.id || u.user_id) === userId ? { ...u, funnel_access: res.data.funnel_access } : u));
    } catch (e) { console.error(e); }
  };


  const updateRole = async (userId, role) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { user_id: userId, role });
            toast.success("Role updated");
            loadUsers();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : JSON.stringify(e.response?.data?.detail) || "Failed"); }
    };

    const sendInvite = async (e) => {
        e.preventDefault();
        if (!inviteForm.email) { toast.error("Email is required"); return; }
        setInviting(true);
        try {
            await api.post("/admin/users/invite", {
                email: inviteForm.email,
                name: inviteForm.name,
                role: inviteForm.role,
                module_access: inviteForm.modules,
            });
            toast.success(`Invite sent to ${inviteForm.email}!`);
            setShowInvite(false);
            setInviteForm(EMPTY_INVITE);
            loadUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to send invite");
        } finally {
            setInviting(false);
        }
    };

    const resetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setResetting(true);
        try {
            await api.post("/auth/admin-set-password", {
                email: resetUser.email,
                new_password: newPassword,
            });
            toast.success(`Password reset for ${resetUser.email}`);
            setResetUser(null);
            setNewPassword("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to reset password");
        } finally {
            setResetting(false);
        }
    };

    const deleteUser = async () => {
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${deleteConfirm.id || deleteConfirm.email}`);
            toast.success(`User ${deleteConfirm.email} deleted`);
            setDeleteConfirm(null);
            loadUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete user");
        } finally {
            setDeleting(false);
        }
    };

    const toggleInviteModule = (mod) => {
        setInviteForm((f) => ({
            ...f,
            modules: f.modules.includes(mod) ? f.modules.filter((m) => m !== mod) : [...f.modules, mod],
        }));
    };

    const pendingUsers = users.filter((u) => !u.status || u.status === "pending");
    const approvedUsers = users.filter((u) => u.status === "approved");
    const revokedUsers = users.filter((u) => u.status === "revoked");
    const tabUsers = tab === "pending" ? pendingUsers : tab === "approved" ? approvedUsers : tab === "revoked" ? revokedUsers : users;
    const filteredUsers = tabUsers.filter((u) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });

    if (loading) {
        return <div className="min-h-screen bg-[var(--s1)] flex items-center justify-center"><div className="animate-pulse text-primary font-heading text-xl">LOADING...</div></div>;
    }

    return (
        <div className="min-h-screen bg-[var(--s1)] p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="font-heading text-2xl text-[var(--t1)] tracking-wide">ADMIN PANEL</h1>
                    <button onClick={() => setShowInvite(true)} className="bg-primary hover:bg-primary/90 text-white rounded-xl px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2">
                        <Mail size={16} /> Invite User
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-[var(--s1)] border border-border rounded-xl text-white text-sm placeholder:text-text-muted focus:outline-none focus:border-primary/50"
                    />
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {[
                        { id: "all", label: `All (${users.length})` },
                        { id: "pending", label: `Pending (${pendingUsers.length})` },
                        { id: "approved", label: `Approved (${approvedUsers.length})` },
                        { id: "revoked", label: `Revoked (${revokedUsers.length})` },
                        { id: "departments", label: `Departments (${departments.length})` },
                        { id: "share-market", label: "Share.market" },
                        { id: "settings", label: "Settings" },
                    ].map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? "bg-primary text-white" : "bg-[var(--s1)] text-text-muted hover:text-[var(--t1)] border border-border"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ═══ SETTINGS TAB ═══ */}
                {tab === "settings" && (
                    <div className="space-y-4 max-w-2xl">
                        <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-xl p-5">
                            <h3 className="text-sm font-semibold text-[var(--t1)] mb-1">First-run hints</h3>
                            <p className="text-xs text-[#888] mb-4">
                                Tooltips and coach-marks across the app to help new users learn the system.
                                Toggles only your own view; team members keep seeing them until they turn theirs off
                                (their setting is per-browser).
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setHintsEnabled(!hintsEnabled)}
                                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                                    style={{
                                        background: hintsEnabled ? "rgba(34,197,94,.15)" : "var(--s2)",
                                        color: hintsEnabled ? "#22C55E" : "var(--t3)",
                                        border: `1px solid ${hintsEnabled ? "rgba(34,197,94,.3)" : "var(--bdr)"}`,
                                    }}
                                >
                                    {hintsEnabled ? "✓ Hints ON" : "Hints OFF"}
                                </button>
                                <button
                                    onClick={() => { resetHints(); toast.success("Dismissed hints reset — they'll show again"); }}
                                    className="px-4 py-2 rounded-lg text-sm font-medium border border-[var(--bdr)] text-[#aaa] hover:text-[var(--t1)]"
                                >
                                    Reset dismissed hints
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ SHARE.MARKET TAB ═══ */}
                {tab === "share-market" && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                            <div>
                                <h3 className="text-base font-semibold text-[var(--t1)]">Share.market creative brief</h3>
                                <p className="text-xs text-[#888] mt-1">
                                    Submissions from <span className="font-mono text-[#aaa]">dentsuamp.com/api/share-market</span>
                                    &nbsp;·&nbsp; Password: <span className="font-mono text-[#aaa]">briefme2026</span>
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setSmLoading(true);
                                        api.get("/admin/share-market/entries")
                                            .then(r => setSmEntries(r.data?.entries || []))
                                            .catch(() => toast.error("Reload failed"))
                                            .finally(() => setSmLoading(false));
                                    }}
                                    className="px-3 py-2 text-xs font-medium border border-[var(--bdr)] rounded-lg text-[#aaa] hover:text-[var(--t1)]"
                                >
                                    {smLoading ? "Loading…" : "Refresh"}
                                </button>
                                <button
                                    onClick={downloadSmCsv}
                                    className="px-3 py-2 text-xs font-semibold bg-[var(--r)] text-white rounded-lg hover:opacity-90"
                                >
                                    Download CSV
                                </button>
                            </div>
                        </div>

                        {smLoading && smEntries.length === 0 ? (
                            <div className="text-center py-12 text-[#888] text-sm">Loading entries…</div>
                        ) : smEntries.length === 0 ? (
                            <div className="text-center py-12 text-[#888] text-sm">
                                No submissions yet. The brief is live at <span className="font-mono">/api/share-market</span>.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#888] border-b border-[var(--bdr)]">
                                            <th className="py-2 pr-3">When</th>
                                            <th className="py-2 pr-3">Name</th>
                                            <th className="py-2 pr-3">Email</th>
                                            <th className="py-2 pr-3">Role</th>
                                            <th className="py-2 pr-3">Persona</th>
                                            <th className="py-2 pr-3">Tone</th>
                                            <th className="py-2 pr-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {smEntries.map((r) => {
                                            const sl = (r.m1 || {}).sliders || {};
                                            const tone = `${sl.serious_playful ?? "–"}/${sl.expert_friend ?? "–"}/${sl.neutral_opinionated ?? "–"}`;
                                            const ts = r.ts ? new Date(r.ts) : null;
                                            const tsTxt = ts ? ts.toLocaleDateString() + " " + ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                                            const isOpen = smExpanded === r.sm_id;
                                            return (
                                                <React.Fragment key={r.sm_id}>
                                                    <tr className="border-b border-[var(--bdr)] hover:bg-[var(--s1)]">
                                                        <td className="py-3 pr-3 text-[#aaa] font-mono text-xs">{tsTxt}</td>
                                                        <td className="py-3 pr-3 text-[var(--t1)] font-medium">{r.name || "—"}</td>
                                                        <td className="py-3 pr-3 text-[#aaa]">{r.email || "—"}</td>
                                                        <td className="py-3 pr-3 text-[#aaa]">{r.role || "—"}</td>
                                                        <td className="py-3 pr-3 text-[var(--t1)]">{r.persona_name || "—"}</td>
                                                        <td className="py-3 pr-3 text-[#aaa] font-mono text-xs">{tone}</td>
                                                        <td className="py-3 whitespace-nowrap">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => setSmExpanded(isOpen ? null : r.sm_id)}
                                                                    className="text-xs text-[#aaa] hover:text-[var(--t1)] underline"
                                                                >
                                                                    {isOpen ? "Hide" : "View"}
                                                                </button>
                                                                {/* Per-entry PDF download — shows the EXACT artifact the
                                                                    respondent saw. Hidden on legacy rows captured before
                                                                    pdf_b64 storage shipped. Uses the api helper so the
                                                                    JWT auth header is attached; downloads via a blob URL
                                                                    so the filename from Content-Disposition wins. */}
                                                                {r.has_pdf && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={async () => {
                                                                            try {
                                                                                const res = await api.get(
                                                                                    `/admin/share-market/entries/${r.sm_id}/pdf`,
                                                                                    { responseType: "blob" }
                                                                                );
                                                                                const url = URL.createObjectURL(res.data);
                                                                                const a = document.createElement("a");
                                                                                a.href = url;
                                                                                const safe = (r.name || "entry").replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "_") || "entry";
                                                                                a.download = `share-market_${safe}_${(r.ts || "").slice(0, 10) || "no-ts"}.pdf`;
                                                                                document.body.appendChild(a); a.click(); a.remove();
                                                                                setTimeout(() => URL.revokeObjectURL(url), 4000);
                                                                            } catch (e) {
                                                                                toast.error(e.response?.data?.detail || "Couldn't download PDF");
                                                                            }
                                                                        }}
                                                                        className="text-xs text-blue-400 hover:text-blue-300 underline"
                                                                        title="Download the rendered PDF"
                                                                    >
                                                                        PDF
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={async () => {
                                                                        if (!window.confirm(`Delete this submission from ${r.name || "this respondent"}? This cannot be undone.`)) return;
                                                                        try {
                                                                            await api.delete(`/admin/share-market/entries/${r.sm_id}`);
                                                                            setSmEntries(prev => prev.filter(x => x.sm_id !== r.sm_id));
                                                                            toast.success("Entry deleted");
                                                                        } catch (e) {
                                                                            toast.error(e.response?.data?.detail || "Delete failed");
                                                                        }
                                                                    }}
                                                                    className="text-xs text-[#888] hover:text-red-400"
                                                                    title="Delete this submission (admin only)"
                                                                >
                                                                    🗑
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isOpen && (
                                                        <tr>
                                                            <td colSpan={7} className="bg-[var(--s1)] p-4 text-xs text-[#aaa] border-b border-[var(--bdr)]">
                                                                <div className="mb-2"><span className="text-[#888]">Persona blurb:</span> {r.persona_blurb || "—"}</div>
                                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                                    <div><div className="text-[#888] text-[10px] uppercase">Audience</div><div>{((r.m1||{}).audience||[]).join(" › ") || "—"}</div></div>
                                                                    <div><div className="text-[#888] text-[10px] uppercase">Brand role</div><div>{(r.m1||{}).role || "—"}</div></div>
                                                                    <div><div className="text-[#888] text-[10px] uppercase">Risk</div><div>{(r.m1||{}).risk || "—"}</div></div>
                                                                </div>
                                                                {r.m2_expanded && Object.keys(r.m2_expanded).length > 0 ? (
                                                                    <div className="mt-3">
                                                                        <div className="text-[#888] text-[10px] uppercase mb-1">Video picks (readable)</div>
                                                                        <div className="space-y-1">
                                                                            {Object.entries(r.m2_expanded).map(([bucket, vids]) => (
                                                                                <div key={bucket} className="text-[11px]">
                                                                                    <span className="text-[var(--t2)] font-medium">{bucket}:</span>{" "}
                                                                                    <span>{Array.isArray(vids) && vids.length ? vids.join(", ") : <span className="text-[#555]">none</span>}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="mt-3">
                                                                        <div className="text-[#888] text-[10px] uppercase mb-1">Bucket picks (raw)</div>
                                                                        <pre className="text-[11px] font-mono whitespace-pre-wrap">{JSON.stringify(r.m2 || {}, null, 2)}</pre>
                                                                    </div>
                                                                )}
                                                                {r.m3 && Object.keys(r.m3).length > 0 && (
                                                                    <div className="mt-3">
                                                                        <div className="text-[#888] text-[10px] uppercase mb-1">Reflections</div>
                                                                        <div className="space-y-1">
                                                                            {Object.entries(r.m3).map(([qid, ans]) => (
                                                                                ans ? (
                                                                                    <div key={qid} className="text-[11px]">
                                                                                        <span className="text-[#888]">{qid}:</span>{" "}
                                                                                        <span className="text-[var(--t2)]">{String(ans).slice(0, 280)}</span>
                                                                                    </div>
                                                                                ) : null
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ═══ DEPARTMENTS TAB ═══ */}
                {tab === "departments" && (
                    <div className="space-y-4">
                        <div className="flex gap-2 items-center">
                            <input value={newDeptName} onChange={e => setNewDeptName(e.target.value)} placeholder="New department name..."
                                onKeyDown={e => e.key === "Enter" && addDepartment()}
                                className="flex-1 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-sm placeholder:text-[var(--t2)] focus:outline-none focus:border-[#E31937]" />
                            <input type="color" value={newDeptColor} onChange={e => setNewDeptColor(e.target.value)} className="w-10 h-10 rounded-lg border border-[var(--bdr)] bg-transparent cursor-pointer" />
                            <button onClick={addDepartment} className="px-4 py-2 bg-[var(--r)] text-white text-sm font-bold rounded-lg hover:bg-[var(--r)]">Add</button>
                        </div>
                        <div className="space-y-2">
                            {departments.map(d => (
                                <div key={d.department_id} className="flex items-center gap-3 px-4 py-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl">
                                    <div className="w-4 h-4 rounded" style={{ background: d.color || "#64748B" }} />
                                    <span className="text-sm text-[var(--t1)] flex-1">{d.name}</span>
                                    <span className="text-xs text-[#888]">{users.filter(u => u.department === d.name).length} members</span>
                                    <button onClick={() => deleteDepartment(d.department_id)} className="text-[#666] hover:text-red-400 transition-colors text-sm">✕</button>
                                </div>
                            ))}
                            {departments.length === 0 && <p className="text-[#888] text-center py-8">No departments yet. Add one above.</p>}
                        </div>
                    </div>
                )}

                {/* User Cards — hidden on the share-market tab (which has its own table) */}
                <div className="space-y-3" style={{ display: tab === "share-market" ? "none" : undefined }}>
                    {filteredUsers.length === 0 && (
                        <p className="text-text-muted text-center py-8">{searchQuery ? "No users match your search" : "No users in this category"}</p>
                    )}
                    {filteredUsers.map((u) => {
                        const uid = u.id || u.user_id || u.email;
                        const isCurrentUser = (u.email || "").toLowerCase() === user?.email?.toLowerCase();
                        const modules = u.module_access || ["hr", "bizdev"];
                        const isAdminEmail = isSuperAdmin(u.email);
                        const status = isAdminEmail ? "approved" : (u.status || "pending");
                        const statusInfo = STATUS_BADGE[status] || STATUS_BADGE.pending;
                        const StatusIcon = statusInfo.icon;

                        return (
                            <div key={uid} data-testid={`admin-user-${uid}`} className="glass-card rounded-2xl p-4 sm:p-5">
                                {/* User info + status */}
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-[var(--s3)] flex items-center justify-center text-white font-bold text-sm">
                                            {(u.name || u.email || "?")[0]?.toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-white text-sm font-medium">{u.name || "Unnamed"}</p>
                                            <div className="flex items-center gap-2">
                                                <p className="text-text-muted text-xs">{u.email}</p>
                                                {u.department && <span className="text-xs px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/15">{u.department}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                            style={{ backgroundColor: statusInfo.color + "20", color: statusInfo.color }}>
                                            <StatusIcon size={12} />
                                            {statusInfo.label}
                                        </div>
                                        {/* Reset Password button */}
                                        <button
                                            onClick={() => { setResetUser(u); setNewPassword(""); }}
                                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                                            title="Reset Password"
                                        >
                                            <Key size={12} /> Reset
                                        </button>
                                        {/* Delete button */}
                                        {!isCurrentUser && !isAdminEmail && (
                                            <button
                                                onClick={() => setDeleteConfirm(u)}
                                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                                                title="Delete User"
                                            >
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Quick approve/deny for pending users */}
                                {status === "pending" && !isAdminEmail && (
                                    <div className="flex gap-2 mb-3">
                                        <button onClick={() => updateStatus(uid, "approved")} data-testid={`approve-${uid}`}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors">
                                            <Check size={14} /> Approve
                                        </button>
                                        <button onClick={() => updateStatus(uid, "revoked")} data-testid={`deny-${uid}`}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors">
                                            <X size={14} /> Deny
                                        </button>
                                    </div>
                                )}

                                {/* Revoked user - re-approve button */}
                                {status === "revoked" && !isAdminEmail && (
                                    <div className="flex gap-2 mb-3">
                                        <button onClick={() => updateStatus(uid, "approved")} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors">
                                            <Check size={14} /> Re-approve
                                        </button>
                                        <button onClick={() => updateStatus(uid, "pending")} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-yellow-400 text-xs font-medium hover:bg-yellow-500/20 transition-colors">
                                            <Clock size={14} /> Set Pending
                                        </button>
                                    </div>
                                )}

                                {/* Approved user - revoke */}
                                {status === "approved" && !isAdminEmail && !isCurrentUser && (
                                    <div className="flex gap-2 mb-3">
                                        <button onClick={() => updateStatus(uid, "revoked")} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors">
                                            <Ban size={14} /> Revoke
                                        </button>
                                    </div>
                                )}

                                {/* Designation — self-set on profile setup (per Dentsu offer letter); admin can set/correct it here */}
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-text-muted text-xs flex-shrink-0">Designation:</span>
                                    <input
                                        key={`desig-${uid}-${u.designation || ""}`}
                                        defaultValue={u.designation || ""}
                                        placeholder="e.g. Creative Director (per offer letter)"
                                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (u.designation || "")) updateDesignation(uid, v); }}
                                        className="text-xs bg-[var(--s1)] border border-[var(--bdr)] rounded-md px-2 py-1 text-white flex-1 max-w-[340px] focus:outline-none focus:border-[#E31937]"
                                    />
                                </div>

                                {/* Module access */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="text-text-muted text-xs">Modules:</span>
                                    {[
                                        { id: "hr", label: "HR", color: "#E31937" },
                                        { id: "bizdev", label: "Biz Dev", color: "#06B6D4" },
                                        { id: "production", label: "Production", color: "#F59E0B" },
                                    ].map((mod) => (
                                        <button key={mod.id} onClick={() => !isCurrentUser && toggleModule(uid, mod.id, modules)}
                                            disabled={isCurrentUser}
                                            data-testid={`module-toggle-${uid}-${mod.id}`}
                                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-all ${
                                                modules.includes(mod.id) ? "font-medium" : "opacity-40 border border-border"
                                            } ${isCurrentUser ? "cursor-not-allowed" : "cursor-pointer hover:opacity-80"}`}
                                            style={{ backgroundColor: modules.includes(mod.id) ? mod.color + "20" : "transparent", color: modules.includes(mod.id) ? mod.color : "#71717A" }}>
                                            {modules.includes(mod.id) ? <Check size={12} /> : <X size={12} />}
                                            {mod.label}
                                        </button>
                                    ))}
                                    {!isCurrentUser && (
                                        <>
                                            <span className="text-text-muted text-xs opacity-40">|</span>
                                            <button onClick={() => setAllModules(uid, ["hr", "bizdev", "production"])}
                                                className="px-2 py-0.5 rounded text-[10px] font-mono tracking-wider text-[var(--t3)] hover:text-[var(--t1)] hover:bg-[var(--s2)] transition-colors"
                                                title="Grant all modules">
                                                ALL
                                            </button>
                                            <button onClick={() => setAllModules(uid, [])}
                                                className="px-2 py-0.5 rounded text-[10px] font-mono tracking-wider text-[var(--t3)] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                                title="Remove all modules">
                                                NONE
                                            </button>
                                        </>
                                    )}
                                </div>
                    {/* Role + Department selectors */}
                    {!isCurrentUser && !isAdminEmail && (
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="text-text-muted text-xs">Role:</span>
                                <select value={u.role || "Member"} onChange={(e) => updateRole(uid, e.target.value)}
                                    className="px-2.5 py-1 bg-[var(--s1)] border border-border rounded-lg text-white text-xs focus:outline-none focus:border-primary/50">
                                    {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-text-muted text-xs">Dept:</span>
                                <select value={u.department || ""} onChange={(e) => updateUserDepartment(uid, e.target.value)}
                                    className="px-2.5 py-1 bg-[var(--s1)] border border-border rounded-lg text-white text-xs focus:outline-none focus:border-primary/50">
                                    <option value="">None</option>
                                    {departments.map(d => <option key={d.department_id} value={d.name}>{d.name}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-text-muted text-xs">Reports To:</span>
                                <select value={u.reports_to || ""} onChange={(e) => updateReportsTo(uid, e.target.value)}
                                    className="px-2.5 py-1 bg-[var(--s1)] border border-border rounded-lg text-white text-xs focus:outline-none focus:border-primary/50">
                                    <option value="">None</option>
                                    {managers.filter(m => (m.user_id || m.id) !== uid).map(m => <option key={m.user_id || m.id} value={m.user_id || m.id}>{m.full_name || m.name} ({m.role})</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                    {(isCurrentUser || isAdminEmail) && (
                        <div className="flex items-center gap-3 mt-2">
                            <span className="text-text-muted text-xs">Role:</span>
                            <span className="text-white text-xs font-medium">{ROLE_OPTIONS.find(r => r.value === (u.role || "Admin"))?.label || u.role}</span>
                        </div>
                    )}
                    {/* Revenue & Financials Access */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <button
                        onClick={() => toggleRevenueAccess(u.id || u.user_id || u.email, u.revenue_access)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "var(--font-mono)",
                          border: u.revenue_access ? "1px solid #10B981" : "1px solid var(--bdr)",
                          background: u.revenue_access ? "rgba(16,185,129,.12)" : "transparent",
                          color: u.revenue_access ? "#10B981" : "var(--t3)",
                          cursor: "pointer",
                          letterSpacing: ".5px",
                          transition: "all .15s"
                        }}
                      >
                        ₹ Revenue & Financials {u.revenue_access ? "✓" : ""}
                      </button>
                    </div>
                    {/* Funnel Access */}
                    <div style={{ marginTop: "6px" }}>
                      <button
                        onClick={() => toggleFunnelAccess(u.user_id || u.email, u.funnel_access)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontFamily: "var(--font-mono)",
                          border: "1px solid var(--bdr)",
                          background: u.funnel_access ? "rgba(16,185,129,.12)" : "transparent",
                          color: u.funnel_access ? "#10B981" : "var(--t3)",
                          cursor: "pointer",
                          letterSpacing: ".5px",
                          transition: "all .15s"
                        }}
                      >
                        🔍 Funnel Data {u.funnel_access ? "✓" : ""}
                      </button>
                    </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Invite User Modal */}
            {showInvite && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--s1)] border border-border rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center gap-3 mb-6">
                            <Mail size={20} className="text-primary" />
                            <h2 className="font-heading text-lg text-[var(--t1)] tracking-wide">INVITE USER</h2>
                            <button onClick={() => { setShowInvite(false); setInviteForm(EMPTY_INVITE); }}
                                className="ml-auto p-1 text-text-muted hover:text-[var(--t1)]">
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={sendInvite} className="space-y-4">
                            <input type="email" placeholder="Email *" value={inviteForm.email}
                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                className="w-full px-4 py-2.5 bg-[var(--s1)] border border-border rounded-xl text-white text-sm placeholder:text-text-muted focus:outline-none focus:border-primary/50" />
                            <input type="text" placeholder="Name" value={inviteForm.name}
                                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                                className="w-full px-4 py-2.5 bg-[var(--s1)] border border-border rounded-xl text-white text-sm placeholder:text-text-muted focus:outline-none focus:border-primary/50" />
                            <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                                className="w-full px-4 py-2.5 bg-[var(--s1)] border border-border rounded-xl text-white text-sm focus:outline-none focus:border-primary/50">
                                {ROLE_OPTIONS.map((r) => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                            <div>
                                <span className="text-text-muted text-xs mb-2 block">Module Access:</span>
                                <div className="flex gap-2">
                                    {[
                                        { id: "hr", label: "HR", color: "#E31937" },
                                        { id: "bizdev", label: "Biz Dev", color: "#06B6D4" },
                                        { id: "production", label: "Production", color: "#F59E0B" },
                                    ].map((mod) => (
                                        <button key={mod.id} type="button" onClick={() => toggleInviteModule(mod.id)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                                                inviteForm.modules.includes(mod.id) ? "font-medium" : "opacity-40 border border-border"
                                            }`}
                                            style={{ backgroundColor: inviteForm.modules.includes(mod.id) ? mod.color + "20" : "transparent",
                                                color: inviteForm.modules.includes(mod.id) ? mod.color : "#71717A" }}>
                                            {inviteForm.modules.includes(mod.id) ? <Check size={12} /> : <X size={12} />}
                                            {mod.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setShowInvite(false); setInviteForm(EMPTY_INVITE); }}
                                    className="flex-1 py-2.5 border border-border rounded-xl text-text-secondary text-sm hover:border-[var(--bdrh)] transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" disabled={inviting}
                                    className="flex-1 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    {inviting ? (<><span className="animate-spin">&#9696;</span> Sending...</>) : (<><Mail size={14} /> Send Invite</>)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {resetUser && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--s1)] border border-border rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center gap-3 mb-4">
                            <Key size={20} className="text-amber-400" />
                            <h2 className="font-heading text-lg text-[var(--t1)] tracking-wide">RESET PASSWORD</h2>
                            <button onClick={() => { setResetUser(null); setNewPassword(""); }}
                                className="ml-auto p-1 text-text-muted hover:text-[var(--t1)]"><X size={18} /></button>
                        </div>
                        <p className="text-text-muted text-sm mb-4">Set a new password for <span className="text-[var(--t1)] font-medium">{resetUser.email}</span></p>
                        <input type="text" placeholder="New password (min 6 characters)" value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-4 py-2.5 bg-[var(--s1)] border border-border rounded-xl text-white text-sm placeholder:text-text-muted focus:outline-none focus:border-amber-500/50 mb-4" />
                        <div className="flex gap-3">
                            <button onClick={() => { setResetUser(null); setNewPassword(""); }}
                                className="flex-1 py-2.5 border border-border rounded-xl text-text-secondary text-sm hover:border-[var(--bdrh)] transition-colors">Cancel</button>
                            <button onClick={resetPassword} disabled={resetting || newPassword.length < 6}
                                className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {resetting ? "Resetting..." : <><Key size={14} /> Set Password</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete User Confirmation */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--s1)] border border-red-500/30 rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center gap-3 mb-4">
                            <Trash2 size={20} className="text-red-400" />
                            <h2 className="font-heading text-lg text-[var(--t1)] tracking-wide">DELETE USER</h2>
                            <button onClick={() => setDeleteConfirm(null)}
                                className="ml-auto p-1 text-text-muted hover:text-[var(--t1)]"><X size={18} /></button>
                        </div>
                        <p className="text-text-muted text-sm mb-1">Are you sure you want to permanently delete:</p>
                        <p className="text-white font-medium text-sm mb-4">{deleteConfirm.name || deleteConfirm.email} ({deleteConfirm.email})</p>
                        <p className="text-red-400 text-xs mb-4">This action cannot be undone.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setDeleteConfirm(null)}
                                className="flex-1 py-2.5 border border-border rounded-xl text-text-secondary text-sm hover:border-[var(--bdrh)] transition-colors">Cancel</button>
                            <button onClick={deleteUser} disabled={deleting}
                                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                {deleting ? "Deleting..." : <><Trash2 size={14} /> Delete User</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
